import React from "react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { Mail } from "lucide-react";
import { openConsentPreferences } from "@/lib/consent";
import SocialLinks from "@/components/SocialLinks";

const LOGO_URL = "/brand/logo.png";

export default function Footer() {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setBusy(true);
        try {
            await http.post("/newsletter/subscribe", { email, source: "footer" });
            setDone(true);
            setEmail("");
            toast.success("Inscription confirmée. Un email de bienvenue arrive.");
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Impossible pour le moment.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <footer className="border-t border-border/40 bg-accent/30 mt-32" data-testid="site-footer">
            <div className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-4 gap-10">
                <div className="md:col-span-2">
                    <div className="flex items-center gap-3 mb-4">
                        <img
                            src={LOGO_URL}
                            alt="Foundation of Light"
                            width="40"
                            height="40"
                            loading="lazy"
                            decoding="async"
                            className="w-10 h-10 object-contain shrink-0"
                        />
                        <span className="font-serif text-2xl">Foundation of Light</span>
                    </div>
                    <p className="text-sm text-muted-foreground max-w-md leading-relaxed mb-6">
                        Un sanctuaire spirituel dédié aux soins énergétiques avec le langage de lumière, aux plans subtils et à l'éveil de conscience.
                        Rejoignez une communauté bienveillante et laissez-vous guider vers votre lumière intérieure.
                    </p>
                    <div>
                        <h4 className="text-sm uppercase tracking-widest text-primary mb-3 font-medium flex items-center gap-2">
                            <Mail className="w-4 h-4" /> Infolettre
                        </h4>
                        {done ? (
                            <p className="text-sm text-foreground/80" data-testid="newsletter-done">
                                Merci — vous êtes inscrit. Vous pouvez vous désinscrire à tout moment depuis n'importe lequel de nos emails.
                            </p>
                        ) : (
                            <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2 max-w-md" data-testid="newsletter-form">
                                <input
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="votre@email.com"
                                    className="h-11 rounded-full px-5 flex-1 bg-background border border-border/60 text-sm outline-none focus:border-primary transition-colors"
                                    data-testid="newsletter-email"
                                    aria-label="Votre adresse email"
                                />
                                <button
                                    type="submit"
                                    disabled={busy}
                                    className="h-11 rounded-full bg-primary text-primary-foreground px-6 text-sm font-medium hover:opacity-90 disabled:opacity-60"
                                    data-testid="newsletter-submit"
                                >
                                    {busy ? "…" : "S'inscrire"}
                                </button>
                            </form>
                        )}
                        <p className="text-xs text-muted-foreground mt-2 max-w-md">
                            En vous inscrivant, vous acceptez notre <Link to="/confidentialite" className="underline decoration-primary/40 underline-offset-4 hover:decoration-primary">politique de confidentialité</Link>. Un lien de désinscription est présent dans chaque email.
                        </p>
                    </div>
                </div>
                <div>
                    <h4 className="text-sm uppercase tracking-widest text-primary mb-4 font-medium">Naviguer</h4>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                        <li>Événements</li>
                        <li>Séances de soin</li>
                        <li>Accompagnements</li>
                        <li>Abonnements</li>
                        <li>Offrir un soin</li>
                    </ul>
                </div>
                <div>
                    <h4 className="text-sm uppercase tracking-widest text-primary mb-4 font-medium">Contact</h4>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                        <li>info@foundation-of-light.com</li>
                        <li>Séances à distance</li>
                        <li>France & International</li>
                    </ul>
                    <div className="mt-6">
                        <div className="text-sm uppercase tracking-widest text-primary mb-3 font-medium">Nous suivre</div>
                        <SocialLinks size="md" testid="footer-social-links" />
                    </div>
                </div>
            </div>
            <div className="border-t border-border/40 py-6 text-xs text-muted-foreground">
                <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-3">
                    <div>© {new Date().getFullYear()} Foundation of Light · Fait avec présence</div>
                    <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2" aria-label="Liens légaux">
                        <Link to="/mentions-legales" className="hover:text-foreground transition-colors" data-testid="footer-impressum">Mentions légales · Impressum</Link>
                        <Link to="/cgv" className="hover:text-foreground transition-colors" data-testid="footer-cgv">CGV · AGB</Link>
                        <Link to="/confidentialite" className="hover:text-foreground transition-colors" data-testid="footer-privacy">Confidentialité · DSGVO</Link>
                        <button
                            type="button"
                            onClick={openConsentPreferences}
                            className="hover:text-foreground transition-colors underline-offset-4 hover:underline decoration-primary/40"
                            data-testid="footer-manage-cookies"
                        >
                            Gérer mes cookies
                        </button>
                    </nav>
                </div>
            </div>
        </footer>
    );
}
